import React, { useEffect, useState } from "react";
import { useData } from "../../../../components/SiteMap/contexts/DataContext";
import { useMapEditing } from "../../../../components/SiteMap/utils/routing";
import StandardButton from "../../../../components/Buttons/StandardButton";
import { PortalButtonHeader } from "./Default";
import { useEditState } from "../../../../components/SiteMap/contexts/EditStateContext";
import { usePaywall } from "../../../../components/SiteMap/contexts/PaywallContext";
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import flash from "../../../../services/flash";
import errors from "../../../../services/errors";

const SiteMapEditPage = () => {
  const { siteMapCreditsUsed, isLoading } = usePaywall();
  const { jobId, season } = useParams<{ jobId: string; season: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && siteMapCreditsUsed) {
      navigate(`/admin/property/${jobId}/site-map/${season}`);
    }
  }, [isLoading, siteMapCreditsUsed, navigate, jobId, season]);

  return <ActualSiteMapEditPage />;
};

export default SiteMapEditPage;

const ActualSiteMapEditPage = () => {
  const { resetMapData, saveMapData } = useData();
  const [saving, setSaving] = useState(false);
  const { setEditing } = useMapEditing();
  const { isDirty, invalidInfo, isInvalid } = useEditState();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { jobId } = useParams();
  const navigate = useNavigate();

  const onCancel = () => {
    resetMapData();
    setEditing(false);
  };
  const onSave = async () => {
    setSaving(true);
    try {
      await saveMapData();
      setEditing(false);
      // Invalidate (and remove) the queries to force the properties detail page to reload fresh data. Here we remove, not just invalidate so that
      // the properties page doesn't load a cached version.
      queryClient.removeQueries({
        predicate: (query) =>
          query.queryKey.find(
            (key) => typeof key === "string" && key.includes("job"),
          ) !== undefined,
      });
      if (searchParams.get("return_to") === "job_update") {
        navigate(`/admin/property/${jobId}`);
      }
      flash.success("Site map saved to property");
    } catch (err) {
      errors.report(err);
      flash.error("Error saving site map - please try saving again");
    } finally {
      setSaving(false);
    }
  };

  const noChanges = !isDirty;

  return (
    <>
      <PortalButtonHeader>
        <button
          onClick={onCancel}
          className="px-6 py-2.5 rounded-xl font-bold text-sm cursor-pointer bg-snow-gray hover:opacity-80 active:opacity-40 mr-4"
        >
          Cancel
        </button>
        <StandardButton
          color="green"
          label="Save"
          className="inline-block"
          onClick={onSave}
          loading={saving}
          disabled={isInvalid || noChanges || saving}
          disabledClick={() => {
            alert(invalidInfo !== "" ? invalidInfo : "No changes made to save");
          }}
        />
      </PortalButtonHeader>
      <Outlet />
    </>
  );
};
